/**
 * PnLSection — P&L waterfall chart + margin analysis + reconciliation.
 */
import { useState, useEffect, useCallback } from 'react';
import { api } from '../../api/client';
import { useUIStore } from '../../stores/uiStore';
import { useFinanceFiltersStore, periodToQs } from '../../stores/financeFiltersStore';
import ReactECharts from 'echarts-for-react';
import { BarChart } from '../../components/charts/BarChart';
import { EntityLink } from '../../components/shared/EntityLink';
import {
  DollarSign, TrendingUp, TrendingDown, AlertTriangle, Loader2,
  BarChart3, PieChart as PieIcon, FileWarning
} from 'lucide-react';

function fmt$(n: number | null | undefined): string {
  if (n == null) return '$0';
  if (Math.abs(n) >= 1000000) return `$${(n / 1000000).toFixed(1)}M`;
  if (Math.abs(n) >= 1000) return `$${(n / 1000).toFixed(1)}K`;
  return `$${n.toFixed(0)}`;
}

function WaterfallChart({ data }: { data: Array<{ name: string; value: number; color: string; isTotal?: boolean }> }) {
  // Build waterfall using stacked bars (invisible base + visible bar)
  const categories = data.map(d => d.name);
  let runningTotal = 0;
  const baseData: number[] = [];
  const barData: Array<{ value: number; itemStyle: { color: string } }> = [];

  for (const d of data) {
    if (d.isTotal) {
      baseData.push(0);
      barData.push({ value: d.value, itemStyle: { color: d.color } });
      runningTotal = d.value;
    } else {
      if (d.value >= 0) {
        baseData.push(runningTotal);
        barData.push({ value: d.value, itemStyle: { color: d.color } });
        runningTotal += d.value;
      } else {
        runningTotal += d.value;
        baseData.push(runningTotal);
        barData.push({ value: Math.abs(d.value), itemStyle: { color: d.color } });
      }
    }
  }

  const option = {
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(30,30,40,0.95)',
      borderColor: 'rgba(255,255,255,0.1)',
      textStyle: { color: '#e5e5e5', fontSize: 12 },
      formatter: (params: any) => {
        const idx = params[0]?.dataIndex;
        const d = data[idx];
        return `<strong>${d.name}</strong><br/>${fmt$(d.value)}`;
      },
    },
    grid: { left: 60, right: 20, top: 10, bottom: 40 },
    xAxis: {
      type: 'category',
      data: categories,
      axisLabel: { color: 'rgba(255,255,255,0.5)', fontSize: 10, rotate: 20 },
      axisLine: { lineStyle: { color: 'rgba(255,255,255,0.1)' } },
    },
    yAxis: {
      type: 'value',
      axisLabel: { color: 'rgba(255,255,255,0.4)', fontSize: 10, formatter: (v: number) => fmt$(v) },
      splitLine: { lineStyle: { color: 'rgba(255,255,255,0.05)' } },
    },
    series: [
      { name: 'Base', type: 'bar', stack: 'total', data: baseData, itemStyle: { color: 'transparent' }, emphasis: { itemStyle: { color: 'transparent' } } },
      { name: 'Value', type: 'bar', stack: 'total', data: barData, barWidth: '50%',
        label: { show: true, position: 'top', formatter: (p: any) => fmt$(data[p.dataIndex].value), fontSize: 10, color: 'rgba(255,255,255,0.6)' },
      },
    ],
  };

  return <ReactECharts option={option} style={{ height: 320 }} notMerge lazyUpdate />;
}

export function PnLSection() {
  const { sellerId } = useUIStore();
  // Período viene del store global (PeriodBar en el header de Finanzas).
  const period = useFinanceFiltersStore(s => s.period);
  const [tab, setTab] = useState<'pnl' | 'margins' | 'recon'>('pnl');
  const [pnl, setPnl] = useState<any>(null);
  const [margins, setMargins] = useState<any[]>([]);
  const [recon, setRecon] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const periodQs = periodToQs(useFinanceFiltersStore.getState().getQuery());
    const [pnlRes, marginsRes, reconRes] = await Promise.all([
      api.get<any>(`/v3/finance/pnl?seller_id=${sellerId}&${periodQs}`),
      api.get<any>(`/v3/finance/margins?seller_id=${sellerId}&limit=20&${periodQs}`),
      api.get<any>(`/v3/finance/reconciliation?seller_id=${sellerId}&${periodQs}`),
    ]);
    if (pnlRes.success && pnlRes.data) setPnl(pnlRes.data);
    if (marginsRes.success && marginsRes.data) setMargins(marginsRes.data as any[]);
    if (reconRes.success && reconRes.data) setRecon(reconRes.data);
    setLoading(false);
  // `period` como dep: cambia en el PeriodBar global → re-fetch.
  }, [sellerId, period]);

  useEffect(() => { load(); }, [load]);

  if (loading && !pnl) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[var(--color-primary)]" /></div>;
  }

  return (
    <div className="space-y-4">
      {/* Header — sub-tabs internos. El selector de período se removió en
          Fase 3: ahora vive en el PeriodBar global del header de Finanzas. */}
      <div className="flex items-center justify-between">
        <div className="flex gap-1 bg-[var(--color-bg-secondary)] p-0.5 rounded-lg">
          {[
            { key: 'pnl' as const, label: 'P&L', icon: <BarChart3 size={14} /> },
            { key: 'margins' as const, label: 'Margins', icon: <TrendingUp size={14} /> },
            { key: 'recon' as const, label: 'Reconciliation', icon: <FileWarning size={14} /> },
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                tab === t.key ? 'bg-[var(--color-primary)] text-white' : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text)]'
              }`}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* P&L Tab */}
      {tab === 'pnl' && pnl && (
        <div className="space-y-4">
          {/* KPI Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-4">
              <p className="text-xs text-[var(--color-text-muted)] uppercase">Gross Revenue</p>
              <p className="text-2xl font-bold text-green-400">{fmt$(pnl.gross_revenue)}</p>
              <p className="text-xs text-[var(--color-text-tertiary)]">{pnl.order_count} orders</p>
            </div>
            <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-4">
              <p className="text-xs text-[var(--color-text-muted)] uppercase">Total Expenses</p>
              <p className="text-2xl font-bold text-red-400">{fmt$(pnl.total_expenses + pnl.ml_fees + pnl.ml_shipping)}</p>
            </div>
            <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-4">
              <p className="text-xs text-[var(--color-text-muted)] uppercase">Net Profit</p>
              <p className={`text-2xl font-bold ${pnl.net_profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>{fmt$(pnl.net_profit)}</p>
            </div>
            <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-4">
              <p className="text-xs text-[var(--color-text-muted)] uppercase">Profit Margin</p>
              <p className={`text-2xl font-bold ${pnl.profit_margin >= 15 ? 'text-green-400' : pnl.profit_margin >= 5 ? 'text-yellow-400' : 'text-red-400'}`}>
                {pnl.profit_margin.toFixed(1)}%
              </p>
            </div>
          </div>

          {/* Waterfall Chart */}
          <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-4">
            <h3 className="text-sm font-medium text-[var(--color-text-secondary)] mb-3">P&L Waterfall</h3>
            <WaterfallChart data={pnl.waterfall} />
          </div>

          {/* Breakdown table */}
          <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-4">
            <h3 className="text-sm font-medium text-[var(--color-text-secondary)] mb-3">Breakdown</h3>
            <table className="w-full text-sm">
              <tbody>
                {[
                  { label: 'Gross Revenue (ML sales)', value: pnl.gross_revenue, color: 'text-green-400' },
                  { label: 'ML Commission Fees', value: -pnl.ml_fees, color: 'text-red-400' },
                  { label: 'ML Shipping Cost', value: -pnl.ml_shipping, color: 'text-yellow-400' },
                  { label: 'Net Revenue', value: pnl.net_revenue, color: 'text-blue-400', bold: true },
                  { label: 'Amazon Product Cost (COGS)', value: -pnl.amazon_cogs, color: 'text-red-400' },
                  { label: 'Amazon Shipping', value: -pnl.amazon_shipping, color: 'text-orange-400' },
                  { label: 'Amazon Tax', value: -pnl.amazon_tax, color: 'text-orange-400' },
                  { label: 'NET PROFIT', value: pnl.net_profit, color: pnl.net_profit >= 0 ? 'text-green-400' : 'text-red-400', bold: true },
                ].map((row, i) => (
                  <tr key={i} className={`border-b border-[var(--color-border)] ${row.bold ? 'bg-[var(--color-bg-tertiary)]' : ''}`}>
                    <td className={`py-2 px-3 ${row.bold ? 'font-bold' : ''} text-[var(--color-text)]`}>{row.label}</td>
                    <td className={`py-2 px-3 text-right font-mono ${row.bold ? 'font-bold text-lg' : ''} ${row.color}`}>
                      {row.value >= 0 ? '' : '-'}${Math.abs(row.value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Margins Tab */}
      {tab === 'margins' && (
        <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[var(--color-bg-tertiary)] text-xs text-[var(--color-text-tertiary)] uppercase">
                <th className="text-left px-3 py-2">Product</th>
                <th className="text-center px-2 py-2">Sales</th>
                <th className="text-right px-2 py-2">Revenue</th>
                <th className="text-right px-2 py-2">Cost</th>
                <th className="text-right px-2 py-2">Profit</th>
                <th className="text-right px-2 py-2">Avg Margin</th>
              </tr>
            </thead>
            <tbody>
              {margins.map((p: any, i: number) => (
                <tr key={i} className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-tertiary)]">
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      {p.thumbnail ? <img src={p.thumbnail} alt="" className="w-8 h-8 rounded object-cover" /> : <div className="w-8 h-8 rounded bg-[var(--color-bg-tertiary)]" />}
                      <div className="min-w-0">
                        <p className="text-[var(--color-text)] truncate max-w-[250px] text-xs">{p.title || p.asin}</p>
                        <p className="text-[10px] font-mono text-teal-500">{p.asin}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-2 py-2 text-center text-[var(--color-text)]">{p.sales}</td>
                  <td className="px-2 py-2 text-right font-mono text-green-400">${Number(p.total_revenue || 0).toFixed(2)}</td>
                  <td className="px-2 py-2 text-right font-mono text-red-400">${Number(p.total_cost || 0).toFixed(2)}</td>
                  <td className="px-2 py-2 text-right font-mono font-bold ${Number(p.total_profit) >= 0 ? 'text-green-400' : 'text-red-400'}">
                    <span className={Number(p.total_profit) >= 0 ? 'text-green-400' : 'text-red-400'}>
                      ${Number(p.total_profit || 0).toFixed(2)}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-right">
                    <span className={`font-bold ${Number(p.avg_margin) >= 20 ? 'text-green-400' : Number(p.avg_margin) >= 10 ? 'text-yellow-400' : 'text-red-400'}`}>
                      {Number(p.avg_margin || 0).toFixed(1)}%
                    </span>
                  </td>
                </tr>
              ))}
              {margins.length === 0 && (
                <tr><td colSpan={6} className="text-center py-12 text-[var(--color-text-tertiary)]">No margin data for this period</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Reconciliation Tab */}
      {tab === 'recon' && recon && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-4">
              <p className="text-xs text-[var(--color-text-muted)] uppercase">Total Orders</p>
              <p className="text-xl font-bold text-[var(--color-text)]">{recon.stats?.total_orders || 0}</p>
            </div>
            <div className="bg-[var(--color-surface)] border border-green-800 rounded-lg p-4">
              <p className="text-xs text-green-400 uppercase">With Purchase</p>
              <p className="text-xl font-bold text-green-400">{recon.stats?.with_purchase || 0}</p>
            </div>
            <div className="bg-[var(--color-surface)] border border-red-800 rounded-lg p-4">
              <p className="text-xs text-red-400 uppercase">Without Purchase</p>
              <p className="text-xl font-bold text-red-400">{recon.stats?.without_purchase || 0}</p>
            </div>
            <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-4">
              <p className="text-xs text-[var(--color-text-muted)] uppercase">Unmatched Revenue</p>
              <p className="text-xl font-bold text-yellow-400">{fmt$(recon.stats?.unmatched_revenue || 0)}</p>
            </div>
          </div>

          {(recon.unmatched_orders || []).length > 0 && (
            <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg overflow-hidden">
              <div className="p-3 border-b border-[var(--color-border)]">
                <h3 className="text-sm font-medium text-[var(--color-text-secondary)]">
                  <AlertTriangle size={14} className="inline mr-1 text-yellow-400" />
                  Orders sin compra vinculada ({recon.unmatched_orders.length})
                </h3>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[var(--color-bg-tertiary)] text-xs text-[var(--color-text-tertiary)] uppercase">
                    <th className="text-left px-3 py-2">ML Order</th>
                    <th className="text-left px-2 py-2">Product</th>
                    <th className="text-left px-2 py-2">Site</th>
                    <th className="text-left px-2 py-2">Date</th>
                    <th className="text-right px-3 py-2">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {recon.unmatched_orders.slice(0, 20).map((o: any, i: number) => (
                    <tr key={i} className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-tertiary)]">
                      <td className="px-3 py-2"><EntityLink type="ml_order" value={String(o.ml_order_id)} /></td>
                      <td className="px-2 py-2 text-xs text-[var(--color-text)] truncate max-w-[200px]">{o.title || o.seller_sku || '—'}</td>
                      <td className="px-2 py-2 text-xs">{o.context_site || '—'}</td>
                      <td className="px-2 py-2 text-xs text-[var(--color-text-secondary)]">
                        {o.date_created ? new Date(o.date_created).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '—'}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-[var(--color-text)]">${Number(o.paid_amount || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
